import { Controller } from "@hotwired/stimulus";
import { Html5Qrcode } from "html5-qrcode";

// Live meal check-in by scanning a student's QR code.
//
// The QR encodes a URL like ".../students/250/qr_info"; we pull the id
// out of the path (never opening the URL) and POST it to the catering
// attendances endpoint as a qr_code check-in. Same workflow/errors as
// the manual form — duplicates and out-of-scope students come back as
// 422 and are surfaced inline so the queue keeps moving.
export default class extends Controller {
  static targets = ["reader", "results", "empty", "mealType", "date", "count"];
  static values = {
    createUrl: String,
    invalidQr: String,
    cameraError: String,
    networkError: String,
    duplicate: String,
    served: String,
  };

  connect() {
    this.recentScans = new Map();
    this.servedCount = 0;
    this.scanner = new Html5Qrcode(this.readerTarget.id);
    this.scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (text) => this.onScan(text),
        () => {}
      )
      .catch(() => this.addRow({ name: this.cameraErrorValue, tone: "error" }));
  }

  disconnect() {
    if (!this.scanner) return;
    this.scanner
      .stop()
      .then(() => this.scanner.clear())
      .catch(() => {});
  }

  onScan(text) {
    const match = String(text).match(/\/students\/(\d+)\/qr_info/);
    if (!match) {
      this.addRow({ name: this.invalidQrValue, tone: "error" });
      return;
    }
    const studentId = match[1];
    const now = Date.now();
    const last = this.recentScans.get(studentId);
    if (last && now - last < 3000) return; // same card within 3s → ignore
    this.recentScans.set(studentId, now);
    this.checkIn(studentId);
  }

  async checkIn(studentId) {
    const body = new FormData();
    body.append("meal_attendance[student_id]", studentId);
    body.append("meal_attendance[date]", this.dateTarget.value);
    body.append("meal_attendance[meal_type]", this.mealTypeTarget.value);
    body.append("meal_attendance[status]", "served");
    body.append("meal_attendance[check_method]", "qr_code");

    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body,
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 201 && data.ok) {
        this.servedCount += 1;
        this.updateCount();
        this.addRow({
          name: data.student_name,
          detail: [data.matricule, data.meal_type, data.served_at]
            .filter(Boolean)
            .join(" · "),
          tone: "success",
          message: this.servedValue,
        });
      } else if (res.status === 422) {
        this.addRow({
          name: data.student_name || `#${studentId}`,
          tone: "warning",
          message: data.error || this.duplicateValue,
        });
      } else {
        this.addRow({
          name: `#${studentId}`,
          tone: "error",
          message: data.error || this.networkErrorValue,
        });
      }
    } catch (e) {
      this.addRow({ name: `#${studentId}`, tone: "error", message: this.networkErrorValue });
    }
  }

  addRow({ name, detail, message, tone }) {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden");

    const tones = {
      success: "bg-green-50 border-green-200 text-green-800",
      warning: "bg-amber-50 border-amber-200 text-amber-800",
      error: "bg-red-50 border-red-200 text-red-800",
    };

    const row = document.createElement("div");
    row.className = `flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${tones[tone]}`;

    const left = document.createElement("div");
    const nameEl = document.createElement("p");
    nameEl.className = "font-semibold";
    nameEl.textContent = name;
    left.appendChild(nameEl);
    if (detail) {
      const detailEl = document.createElement("p");
      detailEl.className = "text-xs opacity-70";
      detailEl.textContent = detail;
      left.appendChild(detailEl);
    }

    const badge = document.createElement("span");
    badge.className = "text-sm font-medium whitespace-nowrap";
    badge.textContent = message || "";

    row.appendChild(left);
    row.appendChild(badge);
    this.resultsTarget.prepend(row);
  }

  updateCount() {
    if (this.hasCountTarget) this.countTarget.textContent = this.servedCount;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
